import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    IconButton,
    Typography,
    Select,
    MenuItem,
    TextField,
    Button,
    List,
    ListItem,
    Grid,
    useTheme,
    useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { getProjectList } from '../../lib/api/salesAction'; 

const ProjectModalContainer = ({ open, onClose, onSelectSchedule }) => {
    const [projectType, setProjectType] = useState('CHANCE'); 
    const [searchType, setSearchType] = useState('title'); 
    const [keyword, setKeyword] = useState(''); 

    const [projects, setProjects] = useState([]); 
    const [loading, setLoading] = useState(false);  
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // 🎯 MUI 반응형 훅 추가 (md 미만 브라우저 크기일 때 모바일 모드로 전환)
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const fetchProjects = useCallback(async () => {
        if (!open) return; 
        
        try {
            setLoading(true);
            const localUser = localStorage.getItem('user');
            let usrUuid = '';

            if (localUser) {
                const parsedUser = JSON.parse(localUser);
                usrUuid = parsedUser.usrUuid || parsedUser.userUuid || '';
            }

            const queryParams = {
                projectType: projectType === 'CHANCE' ? '' : projectType,
                searchType: searchType,
                selectedYear: selectedYear,
                keyword: keyword.trim()
            };
            
            const response = await getProjectList(usrUuid, queryParams);
            if (response && response.data && Array.isArray(response.data.list)) {
                setProjects(response.data.list);
            } else {
                setProjects([]);
            }
        } catch (error) {
            console.error('프로젝트 목록을 불러오는 중 오류가 발생했습니다:', error);
        } finally {
            setLoading(false);
        }
    }, [open, projectType, searchType, selectedYear, keyword]);

    useEffect(() => {
        fetchProjects();
    }, [open]); 

    const handleSearch = () => {
        fetchProjects();
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            fullWidth 
            fullScreen={isMobile} // 🎯 모바일에서는 전체화면 팝업으로 변경
            PaperProps={{
                sx: {
                    width: isMobile ? '100%' : '900px',      
                    height: isMobile ? '100%' : '700px',     
                    maxWidth: 'none',    
                    maxHeight: 'none',
                    borderRadius: isMobile ? '0px' : '12px' 
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                    사업명 선택
                </Typography>
                <IconButton onClick={onClose} aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: '#f8f9fa' }}>
                
                {/* 🎯 검색 조건 박스: 모바일에서는 좁아진 가로폭에 맞춰 flexWrap 처리 */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        flexDirection: 'row',
                        flexWrap: 'wrap', 
                        alignItems: 'center', 
                        gap: 1.5, 
                        p: 2, 
                        backgroundColor: '#ffffff', 
                        borderRadius: '8px', 
                        border: '1px solid #e9ecef', 
                        mb: 3,
                    }}
                >
                    {/* 년도 선택 영역 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: '4px', height: 42, minWidth: { xs: '100%', sm: 130 }, flex: { xs: 1, sm: 'none' }, backgroundColor: '#ffffff' }}>
                        <IconButton onClick={() => setSelectedYear(prev => (prev || new Date().getFullYear()) - 1)} size="small" sx={{ p: 0.75 }}>
                            <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                            value={selectedYear}
                            onChange={(e) => {
                                const value = parseInt(e.target.value, 10);
                                if (!isNaN(value)) {
                                    setSelectedYear(value);
                                } else if (e.target.value === '') {
                                    setSelectedYear('');
                                }
                            }}
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            sx={{ width: '100%', '& input': { textAlign: 'center', fontWeight: 600, fontSize: '15px', p: 0 } }}
                        />
                        <IconButton onClick={() => setSelectedYear(prev => (prev || new Date().getFullYear()) + 1)} size="small" sx={{ p: 0.75 }}>
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* 프로젝트 구분 */}
                    <Select
                        value={projectType}
                        onChange={(e) => setProjectType(e.target.value)}
                        size="small"
                        sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 130 }, height: 42, flex: { xs: 1, sm: 'none' } }}
                    >
                        <MenuItem value="CHANCE">포캐스팅</MenuItem>
                        <MenuItem value="N">프로젝트</MenuItem>
                        <MenuItem value="M">유지보수</MenuItem>
                    </Select>

                    {/* 검색 타입 */}
                    <Select
                        value={searchType}
                        onChange={(e) => setSearchType(e.target.value)}
                        size="small"
                        sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 120 }, height: 42, flex: { xs: 1, sm: 'none' } }}
                    >
                        <MenuItem value="title">사업명</MenuItem>
                        <MenuItem value="manager">담당자</MenuItem> 
                    </Select>

                    {/* 검색어 입력창 */}
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="검색어를 입력해 주세요"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        sx={{ '& .MuiOutlinedInput-root': { height: 42 }, flex: { xs: '1 1 100%', sm: '1' } }}
                    />

                    {/* 검색 버튼 */}
                    <Button 
                        variant="contained" 
                        onClick={handleSearch}
                        startIcon={<SearchIcon />}
                        sx={{ 
                            backgroundColor: '#228be6', 
                            '&:hover': { backgroundColor: '#1c7ed6' }, 
                            minWidth: 90, 
                            width: { xs: '100%', sm: 'auto' }, 
                            height: 42, 
                            boxShadow: 'none', 
                            fontWeight: 600, 
                            whiteSpace: 'nowrap' 
                        }}
                    >
                        검색
                    </Button>
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#495057', px: 0.5 }}>
                    선택 가능한 프로젝트 목록
                </Typography>
                
                {/* 🎯 목록 컨테이너: 모바일일 때 화면 높이에 맞춰 스크롤 영역 유연하게 계산 */}
                <Box sx={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e9ecef', maxHeight: isMobile ? 'calc(100vh - 320px)' : 420, overflowY: 'auto', position: 'relative' }}>
                    
                    {/* 🎯 PC에서만 상단 타이틀 헤더 바를 고정 노출 (모바일은 숨김) */}
                    {!isMobile && (
                        <Box 
                            sx={{ 
                                position: 'sticky', 
                                top: 0, 
                                zIndex: 2, 
                                backgroundColor: '#f1f3f5', 
                                borderBottom: '2px solid #dee2e6',
                                px: 2.5, 
                                py: 1.5 
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={3}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#495057', fontSize: '0.8rem' }}>매출월</Typography>
                                </Grid>
                                <Grid item xs={7}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#495057', fontSize: '0.8rem' }}>사업명</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#495057', fontSize: '0.8rem' }}>담당자</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {Array.isArray(projects) && projects.length > 0 ? (
                        <List disablePadding>
                            {projects.map((project, index) => (
                                <React.Fragment key={project.PRJ_UUID || index}>
                                    <ListItem 
                                        button 
                                        onClick={() => onSelectSchedule(project)} 
                                        sx={{ 
                                            py: 2, 
                                            px: 2.5,
                                            borderBottom: '1px solid #f1f3f5',
                                            '&:hover': { backgroundColor: '#e7f5ff', cursor: 'pointer' }
                                        }}
                                    >
                                        {isMobile ? (
                                            /* 🎯 모바일 전용 UI: 가로가 좁으므로 카드 배지 형태로 데이터 재배치 */
                                            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    {/* 매출월 라벨 강조 */}
                                                    <Typography variant="caption" sx={{ px: 1, py: 0.3, backgroundColor: '#e7f5ff', color: '#1c7ed6', borderRadius: '4px', fontWeight: 700 }}>
                                                        {project.expDay || '날짜 미정'}
                                                    </Typography>
                                                    {/* 담당자 우측 정렬 */}
                                                    <Typography variant="caption" sx={{ color: '#868e96', fontWeight: 500 }}>
                                                        담당: {project.crtNm}
                                                    </Typography>
                                                </Box>
                                                {/* 사업명 대형화 및 말줄임 없이 자동 줄바꿈 */}
                                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1c1e21', mt: 0.5, wordBreak: 'break-all' }}>
                                                    {project.cstNm}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            /* 💻 PC 전용 UI: 기존의 깔끔한 가로 그리드 정렬 유지 */
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={3}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1c1e21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {project.expDay}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1c1e21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {project.cstNm}
                                                    </Typography>
                                                </Grid>                            
                                                <Grid item xs={2}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1c1e21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {project.crtNm}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    ) : (
                        !loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                                <Typography variant="body2" color="text.secondary">조회된 프로젝트 목록이 없습니다.</Typography>
                            </Box>
                        )
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ProjectModalContainer;
